import { ColliClient } from '@frontend/colli/api';
import { Colli } from '@frontend/colli/types';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageType } from '@frontend/package/types';
import { TransactionExtendedClient } from '@frontend/transaction/api';
import { ExtendedTransaction } from '@frontend/transaction/types';

const CLOSED_PICK_UP_STATES = new Set([
    PackageStateName.PACKAGE_PICK_UP_DONE,
    PackageStateName.PACKAGE_PICK_UP_CANCELLED,
    PackageStateName.PACKAGE_PICK_UP_SKIPPED
]);

export function isOpenablePickUpPackage(pkg: Package): boolean {
    return pkg.type === PackageType.PICK_UP && !CLOSED_PICK_UP_STATES.has(pkg.state);
}

/**
 * Openable PICK_UP packages for a transaction at the current SPOT,
 * optionally limited to specific collis.
 */
export async function fetchOpenablePickUpPackages(args: {
    accountId: string;
    transactionId: string;
    spotId?: string | null;
    colliIds?: string[];
}): Promise<Package[]> {
    const response = await PackageClient.fetchPackages({
        account_id: args.accountId,
        transaction_id: args.transactionId,
        spot_id: args.spotId ?? undefined,
        type: PackageType.PICK_UP
    });

    let packages = response.results.filter(isOpenablePickUpPackage);

    if (args.colliIds?.length) {
        const colliIds = new Set(args.colliIds);
        packages = packages.filter((pkg) => pkg.colli_id != null && colliIds.has(pkg.colli_id));
    }

    return packages.sort((a, b) => a.priority - b.priority);
}

/**
 * Resolves transaction + openable pick-up packages for an identified colli at the current SPOT.
 */
export async function resolvePickUpColliContext(args: {
    accountId: string;
    spotId?: string | null;
    colli: Colli;
}): Promise<{ transaction: ExtendedTransaction; collis: Colli[]; packages: Package[] }> {
    if (!args.colli.transaction_id) {
        throw new Error('Colli has no transaction_id');
    }

    const transaction = await TransactionExtendedClient.fetchExtendedTransaction(args.accountId, args.colli.transaction_id);
    const packages = await fetchOpenablePickUpPackages({
        accountId: args.accountId,
        transactionId: args.colli.transaction_id,
        spotId: args.spotId,
        colliIds: [args.colli.id]
    });

    return { transaction, collis: [args.colli], packages };
}

/**
 * Resolves wizard pick-up context from an MQTT/API identified package
 * (e.g. `package_inform_iot_identified` after pin/QR).
 */
export async function resolvePickUpPackageContext(args: {
    accountId: string;
    spotId?: string | null;
    package: Package;
}): Promise<{ transaction: ExtendedTransaction; collis: Colli[]; packages: Package[] }> {
    if (!args.package.transaction_id) {
        throw new Error('Package has no transaction_id');
    }

    if (args.package.colli_id) {
        const colliResult = await ColliClient.resolveCollis([args.package.colli_id]);
        const colli = colliResult.results[0];
        if (colli) {
            const context = await resolvePickUpColliContext({
                accountId: args.accountId,
                spotId: args.spotId,
                colli
            });
            if (context.packages.length > 0) return context;

            return {
                ...context,
                packages: isOpenablePickUpPackage(args.package) ? [args.package] : []
            };
        }
    }

    const transaction = await TransactionExtendedClient.fetchExtendedTransaction(args.accountId, args.package.transaction_id);
    let packages = await fetchOpenablePickUpPackages({
        accountId: args.accountId,
        transactionId: args.package.transaction_id,
        spotId: args.spotId
    });

    if (packages.length === 0 && isOpenablePickUpPackage(args.package)) {
        packages = [args.package];
    }

    return { transaction, collis: [], packages };
}
